import React, { useEffect, useRef, useState } from "react";
import DashboardContent from "../../components/DashboardContent";
import useModalWindow from "../../hooks/useModalWindow";
import useCsr from "../../hooks/useCsr";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CsrAPI } from "../../apis/CsrAPI";
import {
  clearScsSummaryName,
  setScsSummaryName,
} from "../../store/slices/scsDataSlice";
import * as Sentry from "@sentry/react";

const isProduction = process.env.NODE_ENV === "production";
import RedirectHandler from "../../components/RedirectHandler";

const SCSSummary = () => {
  const { studyId: summaryId } = useParams();
  const { modalWindow } = useModalWindow();
  const { setAjaxLoading, wrongEnvironment } = useCsr();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const summaryNameRef = useRef();
  const errRef = useRef();

  const [summaryName, setSummaryName] = useState("");
  const [fetchedSummaryName, setFetchedSummaryName] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [showRedirect, setShowRedirect] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!summaryId) return;

    setAjaxLoading(true);
    CsrAPI.getSafetySummary({ summaryId })
      .then(data => {
        if (!mountedRef.current) return;
        const name = data?.summaryItem?.summaryName || "";
        setFetchedSummaryName(name);
        // Feed the header for the rest of the journey — this is the only
        // place the summary is fetched, later pages just read the store.
        dispatch(setScsSummaryName({ summaryId, summaryName: name }));
        setErrMsg("");
      })
      .catch(error => {
        if (!mountedRef.current) return;
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else if (error.response?.status === 403) {
          setShowRedirect(true);
        } else {
          setErrMsg("Failed to load summary");
        }
      })
      .finally(() => {
        if (mountedRef.current) {
          setAjaxLoading(false);
        }
      });
  }, [summaryId, setAjaxLoading, dispatch]);

  // Landing on /scs/new/summary starts a fresh journey — drop any name left
  // over from a summary visited earlier in the session.
  useEffect(() => {
    if (!summaryId) {
      dispatch(clearScsSummaryName());
    }
  }, [summaryId, dispatch]);

  useEffect(() => {
    if (!errMsg && !summaryId) {
      summaryNameRef.current?.focus();
    }
  }, [errMsg, summaryId]);

  const handleSummaryNameChange = event => {
    setSummaryName(event.target.value);
    // No summary id yet; the create response rebinds the name to its id.
    dispatch(
      setScsSummaryName({ summaryId: null, summaryName: event.target.value })
    );
  };

  useEffect(() => {
    setErrMsg("");
  }, [summaryName]);

  const handleSummary = async event => {
    event.preventDefault();

    try {
      const isConfirmed = await modalWindow({
        title: "Note to the writer",
        message:
          "Note to the writer: You will be asked several questions about the grouping design and table formats. Please be prepared by having available a copy of the source documents. In particular, you will be asked for the name of treatment groups, populations, variables. etc., used in the GIDB column headers or table body (not in the footnote) or in the GIDB titles - you must use the exact wording and spelling (i.e., PIO, PLO, or Placebo; ITT or Intent-to-Treat).",
        confirmButtonLabel: "OK",
      });

      if (isConfirmed) {
        setAjaxLoading(true);

        try {
          const response = await CsrAPI.createSafetySummary({ summaryName });
          const newSummaryId =
            response?.summaryItem?.summaryId ?? response?.summaryId;
          setErrMsg("");

          if (mountedRef.current && newSummaryId) {
            // Bind the typed name to the new id so the header survives the
            // slice reset that fires when the route picks up the summary id.
            dispatch(
              setScsSummaryName({ summaryId: newSummaryId, summaryName })
            );
            navigate("/scs/" + newSummaryId + "/file-upload");
          }
        } catch (error) {
          if (mountedRef.current) {
            if (isProduction) {
              Sentry.captureException(error);
            }
            if (!error?.response) {
              setErrMsg("No Server Response");
            } else if (error.response?.status === 401) {
              setErrMsg("Invalid user credentials");
            } else if (error.response?.status === 403) {
              setShowRedirect(true);
            } else {
              setErrMsg("Fail to create a new summary");
            }
            errRef.current?.focus();
          }
        } finally {
          if (mountedRef.current) {
            setAjaxLoading(false);
          }
        }
      }
    } catch (modalError) {
      if (mountedRef.current) {
        setErrMsg("An unexpected error occurred");
        errRef.current?.focus();
      }
    }
  };

  const PageContent = () => {
    return (
      <div className={"csr-summary csr-summary__wrapper"}>
        <form id={"scs-summary-form"} onSubmit={handleSummary}>
          <span
            ref={errRef}
            className={errMsg ? "errmsg" : "offscreen"}
            aria-live="assertive">
            {errMsg}
          </span>
          <div className={"form-component csr-summary__form-component"}>
            <label htmlFor="summaryname">Summary Name</label>
            {summaryId ? (
              <input
                type="text"
                id="summaryname"
                ref={summaryNameRef}
                autoComplete="off"
                value={fetchedSummaryName}
                required
                aria-invalid={fetchedSummaryName ? "false" : "true"}
                placeholder={"Summary name"}
                className={"input-text"}
                disabled={true}
              />
            ) : (
              <input
                type="text"
                id="summaryname"
                ref={summaryNameRef}
                autoComplete="off"
                onChange={handleSummaryNameChange}
                value={summaryName}
                required
                aria-invalid={summaryName ? "false" : "true"}
                placeholder={"Summary name"}
                className={"input-text"}
              />
            )}
          </div>
        </form>
      </div>
    );
  };

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Type the name of your Summary of Clinical Safety"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={false}
      nextButton={{
        buttonTitle: "Files Upload",
        buttonFormAction: summaryId ? "" : "scs-summary-form",
        buttonGoToAction: summaryId ? "/scs/" + summaryId + "/file-upload" : "",
      }}
    />
  );
};

export default SCSSummary;
